import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Chip,
  Box,
  FormControlLabel,
  Switch,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '@uniqcamp/shared/services/api.service';
import { emailValidationError, mobileDigitsOnly, mobileValidationError, normalizeEmail } from '@uniqcamp/shared';

const SchoolDetails: React.FC = () => {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Record<string, unknown>>({});

  const { data, isLoading } = useQuery(
    ['school', schoolId],
    async () => {
      const response = await apiClient.get(`/schools/${schoolId}`);
      return response.data;
    },
    { onSuccess: (d: { school?: unknown }) => d?.school && setForm(editPayload(d.school as Record<string, unknown>)) }
  );

  function editPayload(school) {
    return {
      name: school.name || '',
      registeredMobile: school.registeredMobile ?? school.contact?.phone ?? '',
      registeredEmail: school.registeredEmail ?? school.contact?.email ?? '',
      logo: school.logo || '',
      address: {
        street: school.address?.street || '',
        city: school.address?.city || '',
        state: school.address?.state || '',
        zipCode: school.address?.zipCode || '',
        country: school.address?.country || '',
      },
      contact: {
        phone: school.registeredMobile ?? school.contact?.phone ?? '',
        email: school.registeredEmail ?? school.contact?.email ?? '',
        website: school.contact?.website || '',
      },
      settings: {
        dispersalStartTime: school.settings?.dispersalStartTime ?? school.settings?.dismissalStartTime ?? '14:00',
        dispersalEndTime: school.settings?.dispersalEndTime ?? school.settings?.dismissalEndTime ?? '16:00',
      },
      paymentStatus: school.paymentStatus || 'PENDING',
      isActive: school.isActive ?? true,
      isBlocked: school.isBlocked ?? false,
    };
  }

  const updateMutation = useMutation(
    async (payload: Record<string, unknown>) => {
      const response = await apiClient.put(`/schools/${schoolId}`, payload);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['school', schoolId]);
        setEditing(false);
      },
    }
  );

  const handleSave = () => {
    const regMobile = String(form.registeredMobile ?? (form.contact as Record<string, unknown>)?.phone ?? '');
    const regEmail = String(form.registeredEmail ?? (form.contact as Record<string, unknown>)?.email ?? '');
    const mobErr = mobileValidationError(regMobile, { required: false, label: 'Registered mobile' });
    if (mobErr) {
      window.alert(mobErr);
      return;
    }
    const emErr = emailValidationError(regEmail, { required: false, label: 'Registered email' });
    if (emErr) {
      window.alert(emErr);
      return;
    }
    const next = { ...form };
    if (regMobile.trim()) {
      const d = mobileDigitsOnly(regMobile);
      next.registeredMobile = d;
      if (next.contact && typeof next.contact === 'object') {
        (next.contact as Record<string, unknown>).phone = d;
      }
    }
    if (regEmail.trim()) {
      const e = normalizeEmail(regEmail);
      next.registeredEmail = e;
      if (next.contact && typeof next.contact === 'object') {
        (next.contact as Record<string, unknown>).email = e;
      }
    }
    updateMutation.mutate(next);
  };

  const handleChange = (path: string, value: unknown) => {
    setForm((prev) => {
      const next = { ...prev };
      const parts = path.split('.');
      let cur: Record<string, unknown> = next;
      for (let i = 0; i < parts.length - 1; i++) {
        const p = parts[i];
        if (!cur[p] || typeof cur[p] !== 'object') cur[p] = {};
        cur = cur[p] as Record<string, unknown>;
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  if (isLoading) return <div>Loading...</div>;
  const school = data?.school as Record<string, unknown> | undefined;
  if (!school) return <Typography>School not found.</Typography>;

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/schools')}>
          Back
        </Button>
        {!editing ? (
          <Button startIcon={<EditIcon />} variant="contained" onClick={() => setEditing(true)}>
            Edit
          </Button>
        ) : (
          <>
            <Button startIcon={<SaveIcon />} variant="contained" onClick={handleSave} disabled={updateMutation.isLoading}>
              Save
            </Button>
            <Button variant="text" onClick={() => { setEditing(false); setForm(editPayload(school)); }}>
              Cancel
            </Button>
          </>
        )}
      </Box>

      <Typography variant="h5" component="h1" fontWeight={600} sx={{ mb: 2 }}>
        {String(school.name ?? '')}
      </Typography>
      <Chip label={String(school.uniqueCode ?? '')} size="small" sx={{ mr: 1 }} />
      {school.onboardingStatus === 'ONBOARDED' && (
        <Chip label="New" color="primary" size="small" sx={{ mr: 1 }} />
      )}
      <Chip label={school.isActive ? 'Active' : 'Inactive'} color={school.isActive ? 'success' : 'default'} size="small" sx={{ mr: 1 }} />
      <Chip label={String(school.paymentStatus ?? '')} size="small" />

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>Details</Typography>
        <Grid container spacing={2}>
          {!editing ? (
            <>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Name</Typography>
                <Typography variant="body1">{String(school.name ?? '')}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Unique Code (immutable)</Typography>
                <Typography variant="body1">{String(school.uniqueCode ?? '')}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Registered Email</Typography>
                <Typography variant="body1">{String(school.registeredEmail ?? (school.contact as Record<string, unknown>)?.email ?? '') || '–'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Registered Mobile</Typography>
                <Typography variant="body1">{String(school.registeredMobile ?? (school.contact as Record<string, unknown>)?.phone ?? '') || '–'}</Typography>
              </Grid>
              {school.logo && (
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">School Logo</Typography>
                  <Box component="img" src={String(school.logo)} alt="School logo" sx={{ maxWidth: 120, maxHeight: 120, mt: 0.5, display: 'block', borderRadius: 1 }} />
                </Grid>
              )}
              {school.onboardingCompletedAt && (
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Onboarded at</Typography>
                  <Typography variant="body1">{new Date(String(school.onboardingCompletedAt)).toLocaleString()}</Typography>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Address</Typography>
                <Typography variant="body1">
                  {[(school.address as Record<string, unknown>)?.street, (school.address as Record<string, unknown>)?.city, (school.address as Record<string, unknown>)?.state, (school.address as Record<string, unknown>)?.zipCode, (school.address as Record<string, unknown>)?.country].filter(Boolean).join(', ') || '–'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Principal</Typography>
                <Typography variant="body1">{school.principalId ? `${String((school.principalId as Record<string, unknown>).name ?? '')} (${String((school.principalId as Record<string, unknown>).mobile ?? '')})` : '–'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Dispersal times</Typography>
                <Typography variant="body1">{String((school.settings as Record<string, unknown>)?.dispersalStartTime ?? (school.settings as Record<string, unknown>)?.dismissalStartTime ?? '')} – {String((school.settings as Record<string, unknown>)?.dispersalEndTime ?? (school.settings as Record<string, unknown>)?.dismissalEndTime ?? '')}</Typography>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Name" value={String(form.name ?? '')} onChange={(e) => handleChange('name', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Registered Email" value={String(form.registeredEmail ?? (form.contact as Record<string, unknown>)?.email ?? '')} onChange={(e) => { handleChange('registeredEmail', e.target.value); handleChange('contact.email', e.target.value); }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Registered Mobile" value={String(form.registeredMobile ?? (form.contact as Record<string, unknown>)?.phone ?? '')} onChange={(e) => { handleChange('registeredMobile', e.target.value); handleChange('contact.phone', e.target.value); }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Website" value={String((form.contact as Record<string, unknown>)?.website ?? '')} onChange={(e) => handleChange('contact.website', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Street" value={String((form.address as Record<string, unknown>)?.street ?? '')} onChange={(e) => handleChange('address.street', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="City" value={String((form.address as Record<string, unknown>)?.city ?? '')} onChange={(e) => handleChange('address.city', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="State" value={String((form.address as Record<string, unknown>)?.state ?? '')} onChange={(e) => handleChange('address.state', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Zip" value={String((form.address as Record<string, unknown>)?.zipCode ?? '')} onChange={(e) => handleChange('address.zipCode', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Country" value={String((form.address as Record<string, unknown>)?.country ?? '')} onChange={(e) => handleChange('address.country', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Dispersal Start" value={String((form.settings as Record<string, unknown>)?.dispersalStartTime ?? (form.settings as Record<string, unknown>)?.dismissalStartTime ?? '')} onChange={(e) => handleChange('settings.dispersalStartTime', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Dispersal End" value={String((form.settings as Record<string, unknown>)?.dispersalEndTime ?? (form.settings as Record<string, unknown>)?.dismissalEndTime ?? '')} onChange={(e) => handleChange('settings.dispersalEndTime', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField select fullWidth label="Payment Status" value={String(form.paymentStatus ?? 'PENDING')} onChange={(e) => handleChange('paymentStatus', e.target.value)} SelectProps={{ native: true }}>
                  <option value="PENDING">PENDING</option>
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="EXPIRED">EXPIRED</option>
                  <option value="SUSPENDED">SUSPENDED</option>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel control={<Switch checked={!!form.isActive} onChange={(e) => handleChange('isActive', e.target.checked)} />} label="Active" />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel control={<Switch checked={!!form.isBlocked} onChange={(e) => handleChange('isBlocked', e.target.checked)} />} label="Blocked" />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

export default SchoolDetails;
